import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import { HeaderMenuBreadcrumb } from '@publicisgroupe/p-os-components';
import ConfigProvider from 'components/ConfigProvider';
import LayoutHeader from 'components/LayoutHeader';
import { ConfigEntityGrid } from '@publicisgroupe/pm-os-config-frontend';

import useWebframePermissions from 'hooks/useWebframePermissions';
import { Configs } from './constants';
import { ConfigItem, ConfigUriParams } from './types';
import ErrorPage from 'components/ErrorPage';

export const ConfigPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { configName } = useParams<ConfigUriParams>();
  const config: ConfigItem | undefined = Configs[configName];
  const { isGranted, isLoading } = useWebframePermissions();
  const [hasPermissions, setHasPermissions] = useState(
    config && !isLoading && isGranted(config.permissionKey)
  );

  const navigationHandler = useCallback((route: string) => history.push(route), [history]);

  const breadcrumbs = [
    {
      name: t('Config'),
      onClick: () => navigationHandler('/Config'),
    },
  ] as HeaderMenuBreadcrumb[];

  useEffect(() => {
    if (!config || isLoading) {
      return;
    }

    setHasPermissions(isGranted(config.permissionKey));
  }, [config, isLoading, isGranted]);

  if (!config || !hasPermissions)
    return (
      <ErrorPage
        headerText={t('NotFound')}
        bodyText={t('ErrorMessage')}
        breadcrumbs={breadcrumbs}
        hideContainerClass
      />
    );

  return (
    <ConfigProvider>
      <LayoutHeader breadcrumbs={breadcrumbs} title={config?.name ?? configName} />
      <main style={{ display: 'flex', flexDirection: 'column' }}>
        <div className="configuration-contents" data-testid={`${configName}-config-entity-grid`}>
          <ConfigEntityGrid entityName={configName} />
        </div>
      </main>
    </ConfigProvider>
  );
};
